import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  getNextQuestion,
  submitAnswer,
  startSession,
  endSession,
  Question as APIQuestion,
  SubmissionResult as APISubmissionResult
} from '../services/api';
import { storage } from '../services/storage';
import { useSoundEffects } from '../hooks/useSoundEffects';

const TOTAL_QUESTIONS = 30;
const TIME_LIMIT_SECONDS = 20 * 60; // 20 minutes

interface Question extends APIQuestion {}
interface SubmissionResult extends APISubmissionResult {}

interface QuestionResult {
  question_id: string;
  prompt: string;
  given_answer: string;
  correct: boolean;
  correct_answer?: string;
  time_taken_seconds: number;
}

interface ExamConfig {
  label: string;
  description: string;
  units: number[];
}

const EXAMS: Record<string, ExamConfig> = {
  midterm: {
    label: 'Midterm Exam',
    description: 'Units 1–4',
    units: [1, 2, 3, 4],
  },
  final: {
    label: 'Final Exam',
    description: 'Units 5–8',
    units: [5, 6, 7, 8],
  },
};

type ChallengeState = 'select' | 'intro' | 'active' | 'complete';

export const TimedChallengePage: React.FC = () => {
  const studentId = storage.getStudentId();

  const [challengeState, setChallengeState] = useState<ChallengeState>('select');
  const [selectedExam, setSelectedExam] = useState<string | null>(null);
  const [question, setQuestion] = useState<Question | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [answer, setAnswer] = useState('');
  const [questionStartTime, setQuestionStartTime] = useState(Date.now());
  const [sessionId, setSessionId] = useState<string | null>(null);

  // Timer state
  const [timeRemaining, setTimeRemaining] = useState(TIME_LIMIT_SECONDS);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Results tracking
  const [results, setResults] = useState<QuestionResult[]>([]);
  const [currentQuestionNum, setCurrentQuestionNum] = useState(0);

  const inputRef = useRef<HTMLInputElement>(null);
  const resultsRef = useRef(results);
  resultsRef.current = results;

  const { playCorrect, playIncorrect, playComplete } = useSoundEffects();

  const examConfig = selectedExam ? EXAMS[selectedExam] : null;

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  // Format time as MM:SS
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Pick a random unit from the exam's unit range
  const getRandomUnit = (): number | undefined => {
    if (!examConfig) return undefined;
    const units = examConfig.units;
    return units[Math.floor(Math.random() * units.length)];
  };

  const selectExam = (examKey: string) => {
    setSelectedExam(examKey);
    setChallengeState('intro');
  };

  // Start the challenge
  const startChallenge = async () => {
    setLoading(true);
    setError(null);

    try {
      // Start a session with the first unit in range as focus
      const focusUnit = examConfig ? examConfig.units[0] : undefined;
      const session = await startSession(studentId, 'challenge', focusUnit);
      setSessionId(session.id);

      // Start timer
      timerRef.current = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            // Time's up! End the challenge
            if (timerRef.current) clearInterval(timerRef.current);
            endChallenge();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      setChallengeState('active');
      fetchNextQuestion();
    } catch (err: any) {
      setError(err.message || 'Failed to start challenge');
      setLoading(false);
    }
  };

  const fetchNextQuestion = async () => {
    setLoading(true);
    setAnswer('');

    try {
      const unit = getRandomUnit();
      const q = await getNextQuestion(studentId, 'practice', unit);
      setQuestion(q);
      setQuestionStartTime(Date.now());
      setCurrentQuestionNum(prev => prev + 1);
    } catch (err: any) {
      setError(err.message || 'Failed to load question');
    } finally {
      setLoading(false);
    }
  };

  // Auto-focus input when new question loads
  useEffect(() => {
    if (question && challengeState === 'active' && inputRef.current) {
      inputRef.current.focus();
    }
  }, [question, challengeState]);

  const handleSubmit = async () => {
    if (!question || !answer.trim()) return;

    const timeTakenMs = Date.now() - questionStartTime;
    const timeTakenSeconds = Math.round(timeTakenMs / 1000);

    try {
      const res = await submitAnswer(
        studentId,
        question.question_id,
        answer,
        timeTakenMs,
        false // no hints in challenge mode
      );

      // Record result
      const result: QuestionResult = {
        question_id: question.question_id,
        prompt: question.prompt,
        given_answer: answer,
        correct: res.correct,
        correct_answer: res.correct_answer,
        time_taken_seconds: timeTakenSeconds,
      };

      setResults(prev => [...prev, result]);

      // Play sound
      if (res.correct) {
        playCorrect();
      } else {
        playIncorrect();
      }

      // Check if challenge is complete
      if (currentQuestionNum >= TOTAL_QUESTIONS) {
        endChallenge();
      } else {
        fetchNextQuestion();
      }
    } catch (err: any) {
      setError(err.message || 'Failed to submit answer');
    }
  };

  const endChallenge = useCallback(async () => {
    // Stop timer
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    // End session
    if (sessionId) {
      const correct = resultsRef.current.filter(r => r.correct).length;
      try {
        await endSession(sessionId, resultsRef.current.length, correct);
      } catch (err) {
        console.error('Failed to end session:', err);
      }
    }

    playComplete();
    setChallengeState('complete');
  }, [sessionId, playComplete]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (challengeState !== 'active') return;

      const target = e.target as HTMLElement;
      const isTyping = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA';

      if (e.key === 'Enter' && isTyping && answer.trim()) {
        e.preventDefault();
        handleSubmit();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [challengeState, answer, handleSubmit]);

  // Calculate results stats
  const calculateStats = () => {
    const correct = results.filter(r => r.correct).length;
    const attempted = results.length;
    const accuracy = attempted > 0 ? Math.round((correct / attempted) * 100) : 0;
    const avgTime = attempted > 0
      ? Math.round(results.reduce((sum, r) => sum + r.time_taken_seconds, 0) / attempted)
      : 0;
    const totalTime = TIME_LIMIT_SECONDS - timeRemaining;

    // Grade based on accuracy
    let grade = 'F';
    if (accuracy >= 90) grade = 'A';
    else if (accuracy >= 80) grade = 'B';
    else if (accuracy >= 70) grade = 'C';
    else if (accuracy >= 60) grade = 'D';

    return { correct, attempted, accuracy, avgTime, totalTime, grade };
  };

  // Exam selection screen
  if (challengeState === 'select') {
    return (
      <div className="timed-challenge-page intro">
        <div className="challenge-intro">
          <h1>Exams</h1>
          <div className="challenge-icon">&#128221;</div>
          <p className="exam-select-subtitle">Choose which exam to take</p>

          <div className="exam-select-grid">
            {Object.entries(EXAMS).map(([key, config]) => (
              <button
                key={key}
                className="exam-select-card"
                onClick={() => selectExam(key)}
              >
                <div className="exam-select-label">{config.label}</div>
                <div className="exam-select-desc">{config.description}</div>
                <div className="exam-select-details">
                  {TOTAL_QUESTIONS} questions &middot; {TIME_LIMIT_SECONDS / 60} minutes
                </div>
              </button>
            ))}
          </div>

          <Link to="/dashboard" className="back-link">
            Back to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  // Intro screen (rules for selected exam)
  if (challengeState === 'intro' && examConfig) {
    return (
      <div className="timed-challenge-page intro">
        <div className="challenge-intro">
          <h1>{examConfig.label}</h1>
          <div className="challenge-icon">&#128221;</div>

          <div className="challenge-rules">
            <h3>Challenge Rules</h3>
            <ul>
              <li><strong>{TOTAL_QUESTIONS} questions</strong> covering {examConfig.description.toLowerCase()}</li>
              <li><strong>{TIME_LIMIT_SECONDS / 60} minutes</strong> time limit</li>
              <li><strong>No hints</strong> allowed</li>
              <li>Questions are drawn randomly from {examConfig.description}</li>
              <li>Your score will be recorded</li>
            </ul>
          </div>

          <div className="challenge-tips">
            <h3>Tips for Success</h3>
            <ul>
              <li>Read each question carefully</li>
              <li>Don't spend too long on any one question</li>
              <li>If stuck, make your best guess and move on</li>
              <li>Average ~40 seconds per question to finish on time</li>
            </ul>
          </div>

          <button className="start-challenge-btn" onClick={startChallenge} disabled={loading}>
            {loading ? 'Starting...' : `Begin ${examConfig.label}`}
          </button>

          <button
            className="back-link"
            onClick={() => { setSelectedExam(null); setChallengeState('select'); }}
            style={{ background: 'none', border: 'none', cursor: 'pointer' }}
          >
            ← Choose a different exam
          </button>
        </div>
      </div>
    );
  }

  // Active challenge
  if (challengeState === 'active') {
    const isLowTime = timeRemaining < 120; // Less than 2 minutes

    return (
      <div className="timed-challenge-page active">
        <div className="challenge-header">
          <div className="challenge-progress">
            <span className="progress-text">
              Question {currentQuestionNum} of {TOTAL_QUESTIONS}
            </span>
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{ width: `${(currentQuestionNum / TOTAL_QUESTIONS) * 100}%` }}
              />
            </div>
          </div>

          <div className={`challenge-timer ${isLowTime ? 'low-time' : ''}`}>
            <span className="timer-icon">&#9201;</span>
            <span className="timer-value">{formatTime(timeRemaining)}</span>
          </div>

          <div className="challenge-score">
            <span className="score-correct">{results.filter(r => r.correct).length}</span>
            <span className="score-divider">/</span>
            <span className="score-total">{results.length}</span>
            <span className="score-label">correct</span>
          </div>
        </div>

        {examConfig && (
          <div className="challenge-exam-label">
            {examConfig.label} — {examConfig.description}
          </div>
        )}

        {loading && !question ? (
          <div className="loading">Loading question...</div>
        ) : error ? (
          <div className="error-message">
            <p>{error}</p>
            <button onClick={fetchNextQuestion}>Try Again</button>
          </div>
        ) : question ? (
          <div className="challenge-question-card">
            <div className="question-prompt">{question.prompt}</div>

            <div className="answer-section">
              {question.answer_type === 'multiple_choice' && question.choices && question.choices.length > 0 ? (
                <div className="mc-choices">
                  {question.choices.map((choice) => (
                    <button
                      key={choice.value}
                      className={`mc-choice-btn ${answer === choice.value ? 'selected' : ''}`}
                      onClick={() => { setAnswer(choice.value); }}
                    >
                      {choice.value}
                    </button>
                  ))}
                </div>
              ) : (
                <input
                  ref={inputRef}
                  type="text"
                  value={answer}
                  onChange={(e) => setAnswer(e.target.value)}
                  placeholder="Enter your answer..."
                  autoComplete="off"
                />
              )}

              <button
                className="submit-btn"
                onClick={handleSubmit}
                disabled={!answer.trim() || loading}
              >
                {loading ? 'Submitting...' : 'Submit'}
              </button>
            </div>

            <div className="keyboard-hint">
              Press <span className="kbd">Enter</span> to submit
            </div>
          </div>
        ) : null}
      </div>
    );
  }

  // Results screen
  const stats = calculateStats();

  return (
    <div className="timed-challenge-page complete">
      <div className="challenge-results">
        <h1>{examConfig ? examConfig.label : 'Challenge'} Complete!</h1>

        <div className={`grade-display grade-${stats.grade}`}>
          <span className="grade-letter">{stats.grade}</span>
          <span className="grade-label">Grade</span>
        </div>

        <div className="results-stats">
          <div className="stat-card">
            <span className="stat-value">{stats.correct}</span>
            <span className="stat-label">Correct</span>
          </div>
          <div className="stat-card">
            <span className="stat-value">{stats.attempted}</span>
            <span className="stat-label">Attempted</span>
          </div>
          <div className="stat-card">
            <span className="stat-value">{stats.accuracy}%</span>
            <span className="stat-label">Accuracy</span>
          </div>
          <div className="stat-card">
            <span className="stat-value">{formatTime(stats.totalTime)}</span>
            <span className="stat-label">Time Used</span>
          </div>
        </div>

        {stats.accuracy >= 80 && (
          <div className="achievement-banner">
            <span className="achievement-icon">&#127942;</span>
            <span className="achievement-text">
              {stats.accuracy >= 90 ? 'Excellent Performance!' : 'Great Job!'}
            </span>
          </div>
        )}

        <div className="results-breakdown">
          <h3>Question Breakdown</h3>
          <div className="breakdown-list">
            {results.map((r, idx) => (
              <div key={idx} className={`breakdown-item ${r.correct ? 'correct' : 'incorrect'}`}>
                <span className="breakdown-num">{idx + 1}</span>
                <span className={`breakdown-icon ${r.correct ? 'correct' : 'incorrect'}`}>
                  {r.correct ? '\u2713' : '\u2717'}
                </span>
                <span className="breakdown-prompt">
                  {r.prompt.length > 60 ? r.prompt.substring(0, 60) + '...' : r.prompt}
                </span>
                {!r.correct && r.correct_answer && (
                  <span className="breakdown-answer">
                    Correct: {r.correct_answer}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="results-actions">
          <button
            className="primary-btn"
            onClick={() => {
              setResults([]);
              setCurrentQuestionNum(0);
              setTimeRemaining(TIME_LIMIT_SECONDS);
              setChallengeState('intro');
            }}
          >
            Try Again
          </button>
          <button
            className="secondary-btn"
            onClick={() => {
              setResults([]);
              setCurrentQuestionNum(0);
              setTimeRemaining(TIME_LIMIT_SECONDS);
              setSelectedExam(null);
              setChallengeState('select');
            }}
          >
            Choose Another Exam
          </button>
          <Link to="/dashboard" className="secondary-btn">
            Back to Dashboard
          </Link>
        </div>
      </div>
    </div>
  );
};

export default TimedChallengePage;
